(function () {
  'use strict';

  if (window.UnifiedTracking) {
    return;
  }

  const CONFIG =
    window.UnifiedTrackingConfig;

  const LEGACY_DEBUG_EVENT_NAME =
    'unified:debug';

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .start === 'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId === 'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .end === 'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta
      );
    }

    return null;
  }

  function reportFatalError(
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      window.UnifiedErrors.report(
        'fatal',
        code,
        message,
        details
      );

      return;
    }

    console.error(
      '[Unified Tracking] ' + message,
      details || {}
    );
  }

  if (!CONFIG) {
    reportFatalError(
      'configuration_unavailable',
      'Configuration unavailable. Check that unified-config.js loads before unified-tracking.js.',
      {
        expectedFile:
          'unified-config.js'
      }
    );

    return;
  }

  function emitLegacyEvent(
    type,
    data
  ) {
    const detail = {
      type: type,

      timestamp:
        new Date().toISOString(),

      data:
        data || {}
    };

    try {
      window.dispatchEvent(
        new CustomEvent(
          LEGACY_DEBUG_EVENT_NAME,
          {
            detail: detail
          }
        )
      );

      return detail;
    } catch (error) {
      console.error(
        '[Unified Tracking] Legacy debug dispatch failed',
        {
          type: type,
          error: error.message
        }
      );

      return null;
    }
  }

  function emitEvent(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data
        );
      } catch (error) {
        reportFatalError(
          'event_bus_emit_failed',
          'Unified Event Bus failed to emit an event.',
          {
            event_type: type,
            error: error.message
          }
        );
      }
    }

    return emitLegacyEvent(
      type,
      data
    );
  }

  function log(
    message,
    data
  ) {
    if (!CONFIG.debug) {
      return;
    }

    console.groupCollapsed(
      '%c[Unified Tracking] ' +
        message,

      'color:#7D664B;font-weight:bold;'
    );

    if (data) {
      console.table(data);
    }

    console.groupEnd();
  }

  function report(
    type,
    message,
    data
  ) {
    emitEvent(
      type,
      {
        message: message,
        details: data || {}
      }
    );

    log(
      message,
      data
    );
  }

  function getFallbackPageData() {
    return {
      page_location:
        window.location.href,

      page_path:
        window.location.pathname,

      page_title:
        document.title,

      page_language:
        document.documentElement.lang ||
        '',

      event_timestamp:
        new Date().toISOString()
    };
  }

  function getContextData() {
    const performanceToken =
      performanceStart(
        'tracking.context'
      );

    if (
      window.UnifiedContext &&
      typeof window.UnifiedContext
        .getContext === 'function'
    ) {
      try {
        const context =
          Object.assign(
            {},
            window.UnifiedContext
              .getContext(),
            {
              event_timestamp:
                new Date().toISOString()
            }
          );

        performanceEnd(
          performanceToken,
          {
            status: 'success'
          }
        );

        return context;
      } catch (error) {
        performanceEnd(
          performanceToken,
          {
            status: 'error'
          }
        );

        report(
          'context_error',
          'Unified Context failed',
          {
            error: error.message
          }
        );
      }
    }

    performanceEnd(
      performanceToken,
      {
        status: 'fallback'
      }
    );

    report(
      'context_unavailable',
      'Unified Context unavailable',
      {
        fallback: 'page_data'
      }
    );

    return getFallbackPageData();
  }

  function createEventId(
    eventName
  ) {
    const normalizedEventName =
      String(
        eventName || 'event'
      )
        .trim()
        .toLowerCase()
        .replace(
          /[^a-z0-9_-]+/g,
          '-'
        )
        .replace(
          /^-+|-+$/g,
          ''
        ) ||
      'event';

    let randomPart = '';

    if (
      window.crypto &&
      typeof window.crypto
        .randomUUID === 'function'
    ) {
      try {
        randomPart =
          window.crypto
            .randomUUID();
      } catch (error) {
        randomPart = '';
      }
    }

    if (
      !randomPart &&
      window.crypto &&
      typeof window.crypto
        .getRandomValues ===
        'function'
    ) {
      try {
        const values =
          new Uint32Array(4);

        window.crypto
          .getRandomValues(
            values
          );

        randomPart =
          Array.from(values)
            .map(function (value) {
              return value
                .toString(16)
                .padStart(
                  8,
                  '0'
                );
            })
            .join('');
      } catch (error) {
        randomPart = '';
      }
    }

    if (!randomPart) {
      randomPart =
        Math.random()
          .toString(36)
          .slice(2) +
        Math.random()
          .toString(36)
          .slice(2);
    }

    return [
      'ut',
      normalizedEventName,
      Date.now()
        .toString(36),
      randomPart
    ].join('-');
  }

  function resolveEventId(
    eventName,
    options
  ) {
    const source =
      options &&
      typeof options === 'object' &&
      !Array.isArray(options)
        ? options
        : {};

    const provided =
      source.event_id ||
      source.eventId ||
      '';

    if (
      typeof provided === 'string' &&
      provided.trim()
    ) {
      return provided.trim();
    }

    return createEventId(
      eventName
    );
  }

  function getCustomerPrivacyApi() {
    if (
      window.Shopify &&
      window.Shopify.customerPrivacy
    ) {
      return window.Shopify.customerPrivacy;
    }

    return null;
  }

  function hasConsentForPurpose(
    purpose
  ) {
    const performanceToken =
      performanceStart(
        'tracking.consent',
        {
          purpose: purpose
        }
      );

    if (!CONFIG.consentRequired) {
      performanceEnd(
        performanceToken,
        {
          allowed: true,
          required: false,
          purpose: purpose
        }
      );

      return true;
    }

    const privacyApi =
      getCustomerPrivacyApi();

    const methodName =
      purpose === 'marketing'
        ? 'marketingAllowed'
        : 'analyticsProcessingAllowed';

    if (
      !privacyApi ||
      typeof privacyApi[
        methodName
      ] !== 'function'
    ) {
      performanceEnd(
        performanceToken,
        {
          allowed: false,
          status: 'unavailable',
          purpose: purpose
        }
      );

      return false;
    }

    try {
      const allowed =
        privacyApi[
          methodName
        ]() === true;

      performanceEnd(
        performanceToken,
        {
          allowed: allowed,
          required: true,
          purpose: purpose
        }
      );

      return allowed;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          status: 'error',
          purpose: purpose
        }
      );

      report(
        'consent_error',
        'Consent check failed',
        {
          purpose: purpose,
          error: error.message
        }
      );

      return false;
    }
  }

  function hasAnalyticsConsent() {
    return hasConsentForPurpose(
      'analytics'
    );
  }

  function hasMarketingConsent() {
    return hasConsentForPurpose(
      'marketing'
    );
  }

  function getProviderConsentPurpose(
    providerId
  ) {
    const configMap =
      CONFIG.providerConsent ||
      CONFIG.provider_consent ||
      {};

    if (
      configMap &&
      typeof configMap === 'object' &&
      !Array.isArray(configMap) &&
      typeof configMap[
        providerId
      ] === 'string'
    ) {
      const configured =
        configMap[
          providerId
        ]
          .trim()
          .toLowerCase();

      if (
        configured === 'analytics' ||
        configured === 'marketing'
      ) {
        return configured;
      }
    }

    const defaults = {
      ga4: 'analytics',
      clarity: 'analytics',
      meta: 'marketing',
      tiktok: 'marketing',
      linkedin: 'marketing'
    };

    return (
      defaults[
        providerId
      ] ||
      'analytics'
    );
  }

  function resolveProviderConsentRoutes(
    routes
  ) {
    const allowedRoutes = [];
    const blockedRoutes = [];

    (
      Array.isArray(routes)
        ? routes
        : []
    ).forEach(function (route) {
      const providerId =
        typeof route === 'string'
          ? route
              .trim()
              .toLowerCase()
          : '';

      if (!providerId) {
        return;
      }

      const purpose =
        getProviderConsentPurpose(
          providerId
        );

      const allowed =
        purpose === 'marketing'
          ? hasMarketingConsent()
          : hasAnalyticsConsent();

      if (allowed) {
        allowedRoutes.push(
          providerId
        );

        return;
      }

      blockedRoutes.push({
        provider: providerId,
        purpose: purpose,
        reason:
          purpose +
          '_consent_missing'
      });
    });

    return {
      requested_routes:
        Array.isArray(routes)
          ? routes.slice()
          : [],
      allowed_routes:
        allowedRoutes,
      blocked_routes:
        blockedRoutes
    };
  }

  function getProviderManager() {
    return (
      window.UnifiedProviderManager ||
      null
    );
  }

  function getProviderRoutes(
    rulesEvaluation
  ) {
    const rulesRoutes =
      rulesEvaluation &&
      Array.isArray(
        rulesEvaluation.routes
      )
        ? rulesEvaluation.routes
        : [];

    const normalizedRoutes = [];
    const seenRoutes = new Set();

    rulesRoutes.forEach(
      function (route) {
        if (typeof route !== 'string') {
          return;
        }

        const normalizedRoute =
          route
            .trim()
            .toLowerCase();

        if (
          !normalizedRoute ||
          seenRoutes.has(
            normalizedRoute
          )
        ) {
          return;
        }

        seenRoutes.add(
          normalizedRoute
        );

        normalizedRoutes.push(
          normalizedRoute
        );
      }
    );

    if (normalizedRoutes.length) {
      return normalizedRoutes;
    }

    return [
      'ga4'
    ];
  }

  function createProviderFailureResult(
    eventName,
    routes,
    code,
    message
  ) {
    return {
      id: '',
      success: false,
      delivered: false,
      partial: false,
      event: eventName || '',
      route_source: 'explicit',
      requested_routes:
        Array.isArray(routes)
          ? routes.slice()
          : [],
      resolved_routes: [],
      providers: {},
      sent_count: 0,
      failed_count: 0,
      skipped_count: 0,
      warnings: [],
      errors: [
        {
          code: code,
          message: message
        }
      ],
      dispatched_at:
        new Date().toISOString()
    };
  }

  function dispatchToProviders(
    eventName,
    payload,
    rulesEvaluation,
    metadata,
    consentResolution
  ) {
    const requestedRoutes =
      getProviderRoutes(
        rulesEvaluation
      );

    const resolvedConsent =
      consentResolution ||
      resolveProviderConsentRoutes(
        requestedRoutes
      );

    const routes =
      resolvedConsent.allowed_routes;

    const performanceToken =
      performanceStart(
        'tracking.providers',
        {
          event: eventName,
          requested_routes:
            requestedRoutes,
          routes: routes,
          consent_blocked_routes:
            resolvedConsent
              .blocked_routes
        }
      );

    if (!routes.length) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'blocked',
          reason:
            'provider_consent_missing'
        }
      );

      const failure =
        createProviderFailureResult(
          eventName,
          requestedRoutes,
          'provider_consent_missing',
          'No requested provider has the required consent.'
        );

      failure.consent_blocked_routes =
        resolvedConsent.blocked_routes;

      return failure;
    }

    const providerManager =
      getProviderManager();

    if (
      !providerManager ||
      typeof providerManager.dispatch !==
        'function'
    ) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'unavailable'
        }
      );

      reportFatalError(
        'provider_manager_unavailable',
        'Unified Provider Manager is unavailable.',
        {
          event: eventName,
          routes: routes
        }
      );

      return createProviderFailureResult(
        eventName,
        routes,
        'provider_manager_unavailable',
        'Unified Provider Manager is unavailable.'
      );
    }

    try {
      const dispatchResult =
        providerManager.dispatch(
          eventName,
          payload,
          {
            routes: routes,
            metadata:
              Object.assign(
                {},
                metadata || {},
                {
                  rules_reason:
                    rulesEvaluation &&
                    rulesEvaluation.reason
                      ? rulesEvaluation.reason
                      : '',
                  rules_explicit_decision:
                    rulesEvaluation &&
                    rulesEvaluation
                      .explicit_decision
                      ? rulesEvaluation
                          .explicit_decision
                      : '',
                  rules_matched:
                    rulesEvaluation &&
                    rulesEvaluation
                      .matched_rules
                      ? rulesEvaluation
                          .matched_rules.length
                      : 0,
                  consent_requested_routes:
                    requestedRoutes,
                  consent_allowed_routes:
                    routes,
                  consent_blocked_routes:
                    resolvedConsent
                      .blocked_routes
                }
              )
          }
        );

      dispatchResult.consent_blocked_routes =
        resolvedConsent.blocked_routes;

      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status:
            dispatchResult.delivered
              ? dispatchResult.partial
                ? 'partial'
                : 'delivered'
              : 'not_delivered',
          sent_count:
            dispatchResult.sent_count ||
            0,
          failed_count:
            dispatchResult.failed_count ||
            0,
          skipped_count:
            dispatchResult.skipped_count ||
            0,
          consent_blocked_count:
            resolvedConsent
              .blocked_routes.length
        }
      );

      return dispatchResult;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'error'
        }
      );

      reportFatalError(
        'provider_dispatch_error',
        'Provider dispatch failed unexpectedly.',
        {
          event: eventName,
          routes: routes,
          error: error.message
        }
      );

      return createProviderFailureResult(
        eventName,
        routes,
        'provider_dispatch_error',
        error.message
      );
    }
  }

  function normalizePayload(
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'tracking.normalization',
        {
          event: eventName
        }
      );

    if (
      !window.UnifiedNormalizer ||
      typeof window.UnifiedNormalizer
        .normalize !== 'function'
    ) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'unavailable'
        }
      );

      return {
        success: false,

        event: eventName,

        payload: payload,

        changed: false,

        changes: [],

        aliases_applied: [],

        warnings: [],

        errors: [
          {
            code:
              'normalizer_unavailable',

            message:
              'Unified Normalizer is unavailable.'
          }
        ]
      };
    }

    try {
      const normalization =
        window.UnifiedNormalizer.normalize(
          eventName,
          payload
        );

      performanceEnd(
        performanceToken,
        {
          event: eventName,

          status:
            normalization.success
              ? 'success'
              : 'failed',

          changed:
            normalization.changed ===
            true,

          changes:
            normalization.changes
              ? normalization.changes.length
              : 0,

          aliases:
            normalization.aliases_applied
              ? normalization.aliases_applied.length
              : 0,

          warnings:
            normalization.warnings
              ? normalization.warnings.length
              : 0
        }
      );

      return normalization;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'error'
        }
      );

      reportFatalError(
        'payload_normalization_error',
        'Payload normalization failed unexpectedly.',
        {
          event: eventName,
          error: error.message
        }
      );

      return {
        success: false,

        event: eventName,

        payload: payload,

        changed: false,

        changes: [],

        aliases_applied: [],

        warnings: [],

        errors: [
          {
            code:
              'payload_normalization_error',

            message:
              error.message
          }
        ]
      };
    }
  }

  function validatePayload(
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'tracking.validation',
        {
          event: eventName
        }
      );

    if (
      !window.UnifiedValidator ||
      typeof window.UnifiedValidator
        .validate !== 'function'
    ) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'unavailable'
        }
      );

      return {
        valid: false,

        event: eventName,

        payload: payload,

        errors: [
          {
            code:
              'schema_validator_unavailable'
          }
        ],

        warnings: []
      };
    }

    try {
      const validation =
        window.UnifiedValidator.validate(
          eventName,
          payload
        );

      performanceEnd(
        performanceToken,
        {
          event: eventName,

          status:
            validation.valid
              ? 'valid'
              : 'invalid',

          errors:
            validation.errors
              ? validation.errors.length
              : 0,

          warnings:
            validation.warnings
              ? validation.warnings.length
              : 0
        }
      );

      return validation;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'error'
        }
      );

      reportFatalError(
        'schema_validation_error',
        'Schema validation failed unexpectedly.',
        {
          event: eventName,
          error: error.message
        }
      );

      return {
        valid: false,

        event: eventName,

        payload: payload,

        errors: [
          {
            code:
              'schema_validation_error',

            message:
              error.message
          }
        ],

        warnings: []
      };
    }
  }

  function evaluateRulesPayload(
    eventName,
    payload,
    options
  ) {
    const performanceToken =
      performanceStart(
        'tracking.rules',
        {
          event: eventName
        }
      );

    if (
      !window.UnifiedRulesEngine ||
      typeof window.UnifiedRulesEngine
        .evaluate !== 'function'
    ) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'unavailable'
        }
      );

      return {
        success: false,

        event: eventName,

        allowed: false,

        blocked: true,

        explicit_decision: '',

        reason:
          'rules_engine_unavailable',

        payload: payload,

        enrichment: {},

        enrichment_changes: [],

        routes: [],

        matched_rules: [],

        decisions: [],

        warnings: [],

        errors: [
          {
            code:
              'rules_engine_unavailable',

            message:
              'Unified Rules Engine is unavailable.'
          }
        ]
      };
    }

    try {
      const evaluation =
        window.UnifiedRulesEngine.evaluate(
          eventName,
          payload,
          options || {}
        );

      performanceEnd(
        performanceToken,
        {
          event: eventName,

          status:
            evaluation.success
              ? 'completed'
              : 'failed',

          allowed:
            evaluation.allowed === true,

          blocked:
            evaluation.blocked === true,

          matched_rules:
            evaluation.matched_rules
              ? evaluation.matched_rules.length
              : 0,

          decisions:
            evaluation.decisions
              ? evaluation.decisions.length
              : 0,

          routes:
            evaluation.routes
              ? evaluation.routes.length
              : 0,

          enrichment_fields:
            evaluation.enrichment
              ? Object.keys(
                  evaluation.enrichment
                ).length
              : 0,

          warnings:
            evaluation.warnings
              ? evaluation.warnings.length
              : 0,

          errors:
            evaluation.errors
              ? evaluation.errors.length
              : 0
        }
      );

      return evaluation;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          event: eventName,
          status: 'error'
        }
      );

      reportFatalError(
        'rules_evaluation_error',
        'Rules evaluation failed unexpectedly.',
        {
          event: eventName,
          error: error.message
        }
      );

      return {
        success: false,

        event: eventName,

        allowed: false,

        blocked: true,

        explicit_decision: '',

        reason:
          'rules_evaluation_error',

        payload: payload,

        enrichment: {},

        enrichment_changes: [],

        routes: [],

        matched_rules: [],

        decisions: [],

        warnings: [],

        errors: [
          {
            code:
              'rules_evaluation_error',

            message:
              error.message
          }
        ]
      };
    }
  }

  function applyRulesEnrichment(
    payload,
    enrichment
  ) {
    const enrichedPayload =
      Object.assign(
        {},
        payload || {}
      );

    const appliedFields = [];
    const protectedFields = [];

    if (
      !enrichment ||
      typeof enrichment !== 'object' ||
      Array.isArray(enrichment)
    ) {
      return {
        payload: enrichedPayload,
        applied_fields: appliedFields,
        protected_fields: protectedFields
      };
    }

    Object.keys(enrichment)
      .forEach(function (fieldName) {
        if (
          typeof enrichedPayload[
            fieldName
          ] !== 'undefined'
        ) {
          protectedFields.push(
            fieldName
          );

          return;
        }

        enrichedPayload[fieldName] =
          enrichment[fieldName];

        appliedFields.push(
          fieldName
        );
      });

    return {
      payload: enrichedPayload,
      applied_fields: appliedFields,
      protected_fields: protectedFields
    };
  }

  function getRulesExecutionOptions(
    options,
    contextData
  ) {
    const source =
      options &&
      typeof options === 'object' &&
      !Array.isArray(options)
        ? options
        : {};

    const rulesOptions = {
      context:
        source.rules_context ||
        source.rulesContext ||
        contextData || {}
    };

    if (Array.isArray(source.rules)) {
      rulesOptions.rules =
        source.rules;
    }

    if (
      typeof source
        .rules_default_decision ===
        'string'
    ) {
      rulesOptions.defaultDecision =
        source.rules_default_decision;
    } else if (
      typeof source
        .rulesDefaultDecision ===
        'string'
    ) {
      rulesOptions.defaultDecision =
        source.rulesDefaultDecision;
    }

    return rulesOptions;
  }

  function track(
    eventName,
    eventData,
    options
  ) {
    const trackPerformanceToken =
      performanceStart(
        'tracking.total',
        {
          event: eventName
        }
      );

    const definition =
      CONFIG.events &&
      CONFIG.events[eventName];

    emitEvent(
      'event_received',
      {
        event: eventName,

        event_data:
          eventData || {}
      }
    );

    if (!definition) {
      report(
        'event_blocked',
        'Unknown event blocked',
        {
          event: eventName,
          reason: 'unknown_event'
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event: eventName,
          status: 'blocked',
          reason: 'unknown_event'
        }
      );

      return false;
    }

    const eventId =
      resolveEventId(
        eventName,
        options
      );

    const contextData =
      getContextData();

    const payload =
      Object.assign(
        {},
        contextData,
        {
          event_category:
            definition.category,

          service:
            definition.service,

          conversion_candidate:
            definition.conversion
        },
        eventData || {}
      );

    emitEvent(
      'event_enriched',
      {
        event: eventName,
        event_id: eventId,
        payload: payload
      }
    );

    const normalization =
      normalizePayload(
        eventName,
        payload
      );

    if (!normalization.success) {
      report(
        'event_blocked',
        'Event blocked by payload normalization',
        {
          event:
            eventName,

          reason:
            'payload_normalization_failed',

          errors:
            normalization.errors || [],

          warnings:
            normalization.warnings || []
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event:
            eventName,

          status:
            'blocked',

          reason:
            'payload_normalization_failed'
        }
      );

      return false;
    }

    const normalizedPayload =
      normalization.payload;

    const validation =
      validatePayload(
        eventName,
        normalizedPayload
      );

    emitEvent(
      'event_validated',
      {
        event:
          eventName,

        valid:
          validation.valid,

        errors:
          validation.errors || [],

        warnings:
          validation.warnings || []
      }
    );

    if (!validation.valid) {
      report(
        'event_blocked',
        'Event blocked by schema validation',
        {
          event:
            eventName,

          reason:
            'schema_validation_failed',

          errors:
            validation.errors || [],

          warnings:
            validation.warnings || []
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event:
            eventName,

          status:
            'blocked',

          reason:
            'schema_validation_failed'
        }
      );

      return false;
    }

    const validatedPayload =
      validation.payload ||
      normalizedPayload;

    const rulesEvaluation =
      evaluateRulesPayload(
        eventName,
        validatedPayload,
        getRulesExecutionOptions(
          options,
          contextData
        )
      );

    if (!rulesEvaluation.success) {
      report(
        'event_blocked',
        'Event blocked by rules evaluation failure',
        {
          event: eventName,

          reason:
            'rules_evaluation_failed',

          rules_reason:
            rulesEvaluation.reason ||
            '',

          errors:
            rulesEvaluation.errors || [],

          warnings:
            rulesEvaluation.warnings || []
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event: eventName,
          status: 'blocked',
          reason:
            'rules_evaluation_failed'
        }
      );

      return false;
    }

    if (
      rulesEvaluation.blocked === true ||
      rulesEvaluation.allowed !== true
    ) {
      report(
        'event_blocked',
        'Event blocked by Rules Engine',
        {
          event: eventName,

          reason:
            rulesEvaluation.reason ||
            'blocked_by_rules',

          explicit_decision:
            rulesEvaluation
              .explicit_decision ||
            '',

          matched_rules:
            rulesEvaluation
              .matched_rules || [],

          decisions:
            rulesEvaluation
              .decisions || [],

          routes:
            rulesEvaluation.routes || [],

          warnings:
            rulesEvaluation.warnings || [],

          errors:
            rulesEvaluation.errors || []
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event: eventName,
          status: 'blocked',
          reason:
            rulesEvaluation.reason ||
            'blocked_by_rules',
          matched_rules:
            rulesEvaluation.matched_rules
              ? rulesEvaluation
                  .matched_rules.length
              : 0
        }
      );

      return false;
    }

    const rulesEnrichment =
      applyRulesEnrichment(
        validatedPayload,
        rulesEvaluation.enrichment
      );

    const approvedPayload =
      rulesEnrichment.payload;

    const requestedProviderRoutes =
      getProviderRoutes(
        rulesEvaluation
      );

    const providerConsent =
      resolveProviderConsentRoutes(
        requestedProviderRoutes
      );

    if (
      providerConsent
        .allowed_routes.length === 0
    ) {
      report(
        'event_blocked',
        'Event blocked by provider consent',
        {
          event: eventName,

          reason:
            'provider_consent_missing',

          requested_routes:
            providerConsent
              .requested_routes,

          blocked_routes:
            providerConsent
              .blocked_routes,

          service:
            approvedPayload.service,

          page_path:
            approvedPayload.page_path,

          ut_session_id:
            approvedPayload.ut_session_id,

          page_sequence:
            approvedPayload.page_sequence
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event: eventName,
          status: 'blocked',
          reason:
            'provider_consent_missing',

          requested_routes:
            providerConsent
              .requested_routes,

          blocked_routes:
            providerConsent
              .blocked_routes.length
        }
      );

      return false;
    }

    emitEvent(
      'event_approved',
      {
        event: eventName,
        event_id: eventId,
        payload: approvedPayload,

        rules: {
          reason:
            rulesEvaluation.reason,

          explicit_decision:
            rulesEvaluation
              .explicit_decision,

          matched_rules:
            rulesEvaluation
              .matched_rules || [],

          decisions:
            rulesEvaluation
              .decisions || [],

          routes:
            rulesEvaluation.routes || [],

          enrichment_fields:
            rulesEnrichment
              .applied_fields,

          protected_fields:
            rulesEnrichment
              .protected_fields
        }
      }
    );

    const providerDispatch =
      dispatchToProviders(
        eventName,
        approvedPayload,
        rulesEvaluation,
        {
          event_id:
            eventId,
          service:
            approvedPayload.service,
          page_path:
            approvedPayload.page_path,
          page_sequence:
            approvedPayload.page_sequence,
          ut_session_id:
            approvedPayload.ut_session_id,
          normalization_changes:
            normalization.changes
              ? normalization.changes.length
              : 0,
          validation_warnings:
            validation.warnings
              ? validation.warnings.length
              : 0,
          rules_routes:
            rulesEvaluation.routes || [],
          rules_enrichment_fields:
            rulesEnrichment.applied_fields
        },
        providerConsent
      );

    if (
      providerDispatch &&
      typeof providerDispatch ===
        'object'
    ) {
      providerDispatch.event_id =
        eventId;
    }

    if (!providerDispatch.delivered) {
      report(
        'event_dispatch_failed',
        'Event was not delivered to any provider',
        {
          event: eventName,
          event_id:
            eventId,
          dispatch_id:
            providerDispatch.id || '',
          requested_routes:
            providerDispatch
              .requested_routes || [],
          resolved_routes:
            providerDispatch
              .resolved_routes || [],
          sent_count:
            providerDispatch.sent_count ||
            0,
          failed_count:
            providerDispatch
              .failed_count || 0,
          skipped_count:
            providerDispatch
              .skipped_count || 0,
          warnings:
            providerDispatch.warnings ||
            [],
          errors:
            providerDispatch.errors || []
        }
      );

      performanceEnd(
        trackPerformanceToken,
        {
          event: eventName,
          status: 'failed',
          reason:
            'provider_delivery_failed',
          sent_count:
            providerDispatch.sent_count ||
            0,
          failed_count:
            providerDispatch
              .failed_count || 0,
          skipped_count:
            providerDispatch
              .skipped_count || 0
        }
      );

      return false;
    }

    report(
      'event_dispatched',
      'Event dispatched',
      {
        event: eventName,

        event_id:
          eventId,

        service:
          approvedPayload.service,

        page_path:
          approvedPayload.page_path,

        page_sequence:
          approvedPayload.page_sequence,

        seconds_since_page_load:
          approvedPayload
            .seconds_since_page_load,

        scroll_depth:
          approvedPayload.scroll_depth,

        normalization_changes:
          normalization.changes
            ? normalization.changes.length
            : 0,

        normalization_warnings:
          normalization.warnings
            ? normalization.warnings.length
            : 0,

        aliases_applied:
          normalization.aliases_applied
            ? normalization.aliases_applied.length
            : 0,

        validation_warnings:
          validation.warnings
            ? validation.warnings.length
            : 0,

        rules_reason:
          rulesEvaluation.reason,

        rules_matched:
          rulesEvaluation.matched_rules
            ? rulesEvaluation
                .matched_rules.length
            : 0,

        rules_decisions:
          rulesEvaluation.decisions
            ? rulesEvaluation
                .decisions.length
            : 0,

        rules_routes:
          rulesEvaluation.routes || [],

        rules_enrichment_applied:
          rulesEnrichment
            .applied_fields.length,

        rules_enrichment_protected:
          rulesEnrichment
            .protected_fields.length,

        provider_dispatch_id:
          providerDispatch.id,

        provider_route_source:
          providerDispatch.route_source,

        provider_requested_routes:
          providerDispatch
            .requested_routes,

        provider_resolved_routes:
          providerDispatch
            .resolved_routes,

        providers_sent:
          providerDispatch.sent_count,

        providers_failed:
          providerDispatch.failed_count,

        providers_skipped:
          providerDispatch
            .skipped_count,

        provider_partial:
          providerDispatch.partial,

        provider_success:
          providerDispatch.success
      }
    );

    performanceEnd(
      trackPerformanceToken,
      {
        event: eventName,
        event_id:
          eventId,
        status:
          providerDispatch.partial
            ? 'partial'
            : 'completed',

        provider_dispatch_id:
          providerDispatch.id,

        providers_sent:
          providerDispatch.sent_count,

        providers_failed:
          providerDispatch.failed_count,

        providers_skipped:
          providerDispatch
            .skipped_count,

        provider_partial:
          providerDispatch.partial,

        normalization_changes:
          normalization.changes
            ? normalization.changes.length
            : 0,

        normalization_warnings:
          normalization.warnings
            ? normalization.warnings.length
            : 0,

        validation_warnings:
          validation.warnings
            ? validation.warnings.length
            : 0,

        rules_matched:
          rulesEvaluation.matched_rules
            ? rulesEvaluation
                .matched_rules.length
            : 0,

        rules_decisions:
          rulesEvaluation.decisions
            ? rulesEvaluation
                .decisions.length
            : 0,

        rules_routes:
          rulesEvaluation.routes
            ? rulesEvaluation.routes.length
            : 0,

        rules_enrichment_applied:
          rulesEnrichment
            .applied_fields.length
      }
    );

    return providerDispatch;
  }

  function enableDebug() {
    CONFIG.debug = true;

    report(
      'debug_status',
      'Debug enabled',
      {
        enabled: true
      }
    );
  }

  function disableDebug() {
    emitEvent(
      'debug_status',
      {
        enabled: false
      }
    );

    CONFIG.debug = false;
  }

  function getConfig() {
    return JSON.parse(
      JSON.stringify(CONFIG)
    );
  }

  function getContext() {
    return getContextData();
  }

  function getErrors() {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors
        .getAll === 'function'
    ) {
      return window.UnifiedErrors
        .getAll();
    }

    return [];
  }

  function getPerformance() {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .getSummary === 'function'
    ) {
      return window.UnifiedPerformance
        .getSummary();
    }

    return null;
  }

  function getBus() {
    return window.UnifiedBus || null;
  }

  function getBusStats() {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus
        .getStats === 'function'
    ) {
      return window.UnifiedBus
        .getStats();
    }

    return null;
  }

  function getSchema() {
    return window.UnifiedSchema || null;
  }

  function getNormalizer() {
    return window.UnifiedNormalizer || null;
  }

  function getValidator() {
    return window.UnifiedValidator || null;
  }

  function getRulesEngine() {
    return window.UnifiedRulesEngine || null;
  }

  function evaluateRules(
    eventName,
    payload,
    options
  ) {
    return evaluateRulesPayload(
      eventName,
      payload,
      options
    );
  }

  function dispatchProviders(
    eventName,
    payload,
    options
  ) {
    const providerManager =
      getProviderManager();

    if (
      !providerManager ||
      typeof providerManager.dispatch !==
        'function'
    ) {
      return createProviderFailureResult(
        eventName,
        options &&
        Array.isArray(options.routes)
          ? options.routes
          : [],
        'provider_manager_unavailable',
        'Unified Provider Manager is unavailable.'
      );
    }

    return providerManager.dispatch(
      eventName,
      payload,
      options || {}
    );
  }

  function normalize(
    eventName,
    payload
  ) {
    return normalizePayload(
      eventName,
      payload
    );
  }

  function validate(
    eventName,
    payload
  ) {
    return validatePayload(
      eventName,
      payload
    );
  }

  window.UnifiedTracking = {
    version:
      CONFIG.version,

    config:
      CONFIG,

    track:
      track,

    normalize:
      normalize,

    validate:
      validate,

    evaluateRules:
      evaluateRules,

    enableDebug:
      enableDebug,

    disableDebug:
      disableDebug,

    getConfig:
      getConfig,

    getContext:
      getContext,

    getErrors:
      getErrors,

    getPerformance:
      getPerformance,

    getBus:
      getBus,

    getBusStats:
      getBusStats,

    getSchema:
      getSchema,

    getNormalizer:
      getNormalizer,

    getValidator:
      getValidator,

    getRulesEngine:
      getRulesEngine,

    getProviderManager:
      getProviderManager,

    dispatchProviders:
      dispatchProviders,

    hasAnalyticsConsent:
      hasAnalyticsConsent,

    hasMarketingConsent:
      hasMarketingConsent,

    getProviderConsentPurpose:
      getProviderConsentPurpose,

    resolveProviderConsentRoutes:
      resolveProviderConsentRoutes,

    emitDebug:
      emitEvent,

    emit:
      emitEvent,

    debugEventName:
      LEGACY_DEBUG_EVENT_NAME
  };

  report(
    'framework_initialized',
    'Framework initialized',
    {
      version:
        CONFIG.version,

      debug:
        CONFIG.debug,

      consentRequired:
        CONFIG.consentRequired,

      schema:
        window.UnifiedSchema
          ? 'available'
          : 'unavailable',

      normalizer:
        window.UnifiedNormalizer
          ? 'available'
          : 'unavailable',

      validator:
        window.UnifiedValidator
          ? 'available'
          : 'unavailable',

      rulesEngine:
        window.UnifiedRulesEngine
          ? 'available'
          : 'unavailable',

      providerManager:
        window.UnifiedProviderManager
          ? 'available'
          : 'unavailable',

      registeredProviders:
        window.UnifiedProviderManager &&
        typeof window.UnifiedProviderManager
          .getProviders === 'function'
          ? window.UnifiedProviderManager
              .getProviders()
              .map(function (provider) {
                return provider.id;
              })
          : [],

      bus:
        window.UnifiedBus
          ? 'available'
          : 'fallback',

      context:
        window.UnifiedContext
          ? 'available'
          : 'unavailable',

      errors:
        window.UnifiedErrors
          ? 'available'
          : 'unavailable',

      journey:
        window.UnifiedJourney
          ? 'available'
          : 'unavailable',

      performance:
        window.UnifiedPerformance
          ? 'available'
          : 'unavailable',

      defaultProviderRoute:
        'ga4',

      ga4Configured:
        CONFIG.providers &&
        CONFIG.providers.ga4 === true
          ? 'enabled'
          : 'disabled'
    }
  );
})();
